import { useAuth } from "@/context/AuthContext";
import { useEffect, useRef, useState } from "react";
import { getScripts, updateScript, createScript, deleteScript, getObjections, createObjection, updateObjection, deleteObjection, resetContent, getTemplates, createTemplate, updateTemplate, deleteTemplate } from "@/lib/apiClient";
import { Save, Plus, Trash2, RotateCcw, FileText, MessageSquareQuote, Send, GripVertical, Users2, SlidersHorizontal, Image as ImageIcon, Package, BookOpen, PhoneCall, Upload } from "lucide-react";
import { RinkelPanel, RinkelBadge } from "@/components/RinkelPanel";
import { LeadImportPanel } from "@/components/LeadImportPanel";
import { getRinkelStatus } from "@/lib/apiClient";
import { toast } from "sonner";
import { StageCard, checkRoutes } from "@/components/ScriptStageEditor";
import { ScriptVersions } from "@/components/ScriptVersions";
import { TeamUserEditor, PipelineConfigEditor, BrandingEditor, ServiceEditor, KbEditor } from "./BeheerAdmin";

const slugId = (p) => `${p}-${Math.random().toString(36).slice(2, 9)}`;

function ScriptEditor() {
  const [scripts, setScripts] = useState([]);
  const [active, setActive] = useState(0);
  const [saving, setSaving] = useState(false);
  const dragScript = useRef(null);
  const dragStage = useRef(null);

  const load = () => getScripts().then((s) => { setScripts(s); setActive((a) => Math.min(a, Math.max(0, s.length - 1))); }).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!scripts.length) return <p className="text-[#5B6675]">Laden…</p>;
  const script = scripts[active];
  const issues = checkRoutes(script);

  const patch = (updater) => setScripts((all) => all.map((s, i) => (i === active ? updater(s) : s)));
  const setField = (k, v) => patch((s) => ({ ...s, [k]: v }));
  const setStage = (si, k, v) => patch((s) => ({ ...s, stages: s.stages.map((st, i) => (i === si ? { ...st, [k]: v } : st)) }));
  const setLines = (si, text) => setStage(si, "lines", text.split("\n"));

  const addStage = () => patch((s) => ({ ...s, stages: [...s.stages, { id: slugId("stage"), label: "Nieuwe stap", goal: "", lines: [""] }] }));
  const removeStage = (si) => {
    const st = script.stages[si];
    const refs = script.stages.flatMap((x) => (x.branches || []).filter((b) => b.target === st.id).map((b) => `• ${x.label}: "${b.label}"`));
    const msg = refs.length ? `Stap "${st.label}" verwijderen?\n\nLet op: deze antwoorden verwijzen naar deze stap en krijgen dan geen vervolgstap meer:\n${refs.join("\n")}` : `Stap "${st.label}" verwijderen?`;
    if (window.confirm(msg)) patch((s) => ({ ...s, stages: s.stages.filter((_, i) => i !== si) }));
  };

  const reorderStages = (to) => {
    const from = dragStage.current;
    if (from == null || from === to) return;
    patch((s) => { const arr = [...s.stages]; const [m] = arr.splice(from, 1); arr.splice(to, 0, m); return { ...s, stages: arr }; });
    dragStage.current = null;
  };

  const save = async () => {
    const errors = checkRoutes(script).filter((x) => x.level === "error");
    if (errors.length) { toast.error(`Niet opgeslagen: ${errors.length} antwoord(en) zonder geldige vervolgstap of tekst. Zie de rode meldingen.`); return; }
    setSaving(true);
    try {
      const clean = { ...script, stages: script.stages.map((st) => ({ ...st, lines: (st.lines || []).filter((l) => l.trim() !== ""),
        branches: (st.branches || []).map((b) => ({ ...b, label: b.label.trim() })) })) };
      await updateScript(script.id, clean);
      toast.success("Belscript opgeslagen (vorige versie bewaard)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
    finally { setSaving(false); }
  };

  const addScript = async () => {
    const id = slugId("script");
    const newScript = { id, title: "Nieuw belscript", subtitle: "Beschrijving", accent: "primary", order: scripts.length, stages: [{ id: slugId("stage"), label: "Opening", goal: "", lines: [""] }] };
    try {
      await createScript(newScript);
      const s = await getScripts();
      setScripts(s);
      setActive(s.findIndex((x) => x.id === id));
      toast.success("Belscript toegevoegd");
    } catch { toast.error("Toevoegen mislukt"); }
  };

  const removeScript = async () => {
    if (scripts.length <= 1) { toast.error("Minstens één belscript is vereist"); return; }
    if (!window.confirm(`Belscript "${script.title}" verwijderen?`)) return;
    try {
      await deleteScript(script.id);
      const s = await getScripts();
      setScripts(s); setActive(0);
      toast.success("Belscript verwijderd");
    } catch { toast.error("Verwijderen mislukt"); }
  };

  const reorderScripts = async (to) => {
    const from = dragScript.current;
    if (from == null || from === to) return;
    const arr = [...scripts];
    const [m] = arr.splice(from, 1);
    arr.splice(to, 0, m);
    const reindexed = arr.map((s, i) => ({ ...s, order: i }));
    setScripts(reindexed);
    setActive(to);
    dragScript.current = null;
    try { await Promise.all(reindexed.map((s) => updateScript(s.id, { order: s.order }))); toast.success("Volgorde opgeslagen"); }
    catch { toast.error("Volgorde opslaan mislukt"); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex flex-wrap items-center gap-2">
          {scripts.map((s, i) => (
            <button key={s.id} data-testid={`edit-script-tab-${s.id}`}
              draggable onDragStart={() => (dragScript.current = i)} onDragOver={(e) => e.preventDefault()} onDrop={() => reorderScripts(i)}
              onClick={() => setActive(i)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-grab active:cursor-grabbing ${active === i ? "bg-[#001C51] text-white" : "bg-white border border-[#E2E7EC] text-[#5B6675] hover:bg-[#EDF1F5]"}`}>
              <GripVertical className="h-3.5 w-3.5 opacity-60" /> {s.title}
            </button>
          ))}
          <button data-testid="add-script" onClick={addScript} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-[#001C51] border border-dashed border-[#001C51]/40 hover:bg-[#E3E9F5]">
            <Plus className="h-4 w-4" /> Script
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="delete-script" onClick={removeScript} className="p-2.5 rounded-xl border border-[#E2E7EC] text-[#B0413E] hover:bg-[#F3DEDD]" title="Belscript verwijderen"><Trash2 className="h-4 w-4" /></button>
          <button data-testid="save-script" onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#001C51] text-white font-medium hover:bg-[#0A337A] disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? "Opslaan…" : "Belscript opslaan"}
          </button>
        </div>
      </div>

      <p className="text-xs text-[#5B6675] mb-3">Sleep de scripttabs of de stappen (met het greepje) om ze te herordenen.</p>

      <div className="space-y-4">
        <div className="rounded-2xl border border-[#E2E7EC] bg-white p-5 grid gap-3 sm:grid-cols-2">
          <label className="block"><span className="text-sm text-[#5B6675] mb-1.5 block">Titel</span>
            <input data-testid="script-title" className="fe-input" value={script.title} onChange={(e) => setField("title", e.target.value)} /></label>
          <label className="block"><span className="text-sm text-[#5B6675] mb-1.5 block">Ondertitel</span>
            <input data-testid="script-subtitle" className="fe-input" value={script.subtitle || ""} onChange={(e) => setField("subtitle", e.target.value)} /></label>
        </div>

        <ScriptVersions script={script} onLoad={(doc) => patch(() => ({ ...doc, id: script.id }))} />
        {script.stages.map((st, si) => (
          <StageCard key={`${script.id}-${st.id}-${si}`} st={st} si={si} stages={script.stages} issues={issues.filter((x) => x.stage === st.id)}
            setStage={(k, v) => setStage(si, k, v)} setLines={(t) => setLines(si, t)} onDelete={() => removeStage(si)}
            onDragStart={() => (dragStage.current = si)} onDrop={() => reorderStages(si)} />
        ))}

        <button data-testid="add-stage" onClick={addStage} className="w-full py-3 rounded-2xl border border-dashed border-[#001C51]/40 text-[#001C51] font-medium hover:bg-[#E3E9F5] inline-flex items-center justify-center gap-2">
          <Plus className="h-4 w-4" /> Stap toevoegen
        </button>
      </div>
    </div>
  );
}

function ObjectionEditor() {
  const [items, setItems] = useState([]);
  const load = () => getObjections().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (id, k, v) => setItems((all) => all.map((o) => (o.id === id ? { ...o, [k]: v } : o)));

  const save = async (o) => {
    try { await updateObjection(o.id, o); toast.success("Antwoord opgeslagen"); }
    catch { toast.error("Opslaan mislukt"); }
  };
  const add = async () => {
    const res = await createObjection({ question: "Nieuwe vraag", answer: "Uw antwoord…", tags: [] });
    toast.success("Antwoord toegevoegd");
    load();
  };
  const remove = async (id) => { await deleteObjection(id); setItems((all) => all.filter((o) => o.id !== id)); toast.success("Verwijderd"); };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button data-testid="add-objection" onClick={add} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#001C51] text-white font-medium hover:bg-[#0A337A]">
          <Plus className="h-4 w-4" /> Nieuw antwoord
        </button>
      </div>
      <div className="space-y-3">
        {items.map((o) => (
          <div key={o.id} className="rounded-2xl border border-[#E2E7EC] bg-white p-5" data-testid={`edit-objection-${o.id}`}>
            <input className="fe-input font-semibold mb-2" value={o.question} onChange={(e) => set(o.id, "question", e.target.value)} placeholder="Vraag / bezwaar" />
            <textarea className="fe-input resize-y leading-relaxed" rows={3} value={o.answer} onChange={(e) => set(o.id, "answer", e.target.value)} placeholder="Antwoord" />
            <div className="flex justify-end gap-2 mt-3">
              <button data-testid={`del-objection-${o.id}`} onClick={() => remove(o.id)} className="p-2 rounded-lg hover:bg-[#F3DEDD] text-[#B0413E]"><Trash2 className="h-4 w-4" /></button>
              <button data-testid={`save-objection-${o.id}`} onClick={() => save(o)} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#001C51] text-white text-sm font-medium hover:bg-[#0A337A]"><Save className="h-4 w-4" /> Opslaan</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const AUDIENCE_LABELS = { owner: "Eigenaar", buyer: "Koper", renter: "Huurder", partner: "Partner", all: "Algemeen" };

function TemplateEditor() {
  const [items, setItems] = useState([]);
  const load = () => getTemplates().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (id, k, v) => setItems((all) => all.map((t) => (t.id === id ? { ...t, [k]: v } : t)));
  const save = async (t) => {
    try { await updateTemplate(t.id, t); toast.success("Bericht opgeslagen"); }
    catch { toast.error("Opslaan mislukt"); }
  };
  const add = async () => {
    await createTemplate({ channel: "whatsapp", audience: "owner", title: "Nieuw bericht", subject: "", body: "Hallo {{naam}}, ..." });
    toast.success("Bericht toegevoegd");
    load();
  };
  const remove = async (id) => { await deleteTemplate(id); setItems((all) => all.filter((t) => t.id !== id)); toast.success("Verwijderd"); };

  return (
    <div>
      <div className="rounded-xl bg-[#F6F9FB] border border-[#E2E7EC] p-4 text-sm text-[#5B6675] leading-relaxed mb-4">
        Gebruik <b>{"{{naam}}"}</b> voor de naam van de lead (wordt automatisch ingevuld). Vervang de links
        <b> [LINK-BROCHURE]</b>, <b>[LINK-PAKKETTEN]</b>, <b>[LINK-FUNDA]</b>, <b>[LINK-WEBSITE]</b> en <b>[JOUW-NAAM]</b> door je eigen tekst.
      </div>
      <div className="flex justify-end mb-4">
        <button data-testid="add-template" onClick={add} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#001C51] text-white font-medium hover:bg-[#0A337A]">
          <Plus className="h-4 w-4" /> Nieuw bericht
        </button>
      </div>
      <div className="space-y-3">
        {items.map((t) => (
          <div key={t.id} className="rounded-2xl border border-[#E2E7EC] bg-white p-5" data-testid={`edit-template-${t.id}`}>
            <div className="grid gap-3 sm:grid-cols-3 mb-3">
              <label className="block"><span className="text-sm text-[#5B6675] mb-1.5 block">Kanaal</span>
                <select className="fe-input" value={t.channel} onChange={(e) => set(t.id, "channel", e.target.value)}>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="email">E-mail</option>
                </select></label>
              <label className="block"><span className="text-sm text-[#5B6675] mb-1.5 block">Voor wie</span>
                <select className="fe-input" value={t.audience} onChange={(e) => set(t.id, "audience", e.target.value)}>
                  {Object.entries(AUDIENCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select></label>
              <label className="block"><span className="text-sm text-[#5B6675] mb-1.5 block">Titel</span>
                <input className="fe-input" value={t.title} onChange={(e) => set(t.id, "title", e.target.value)} /></label>
            </div>
            {t.channel === "email" && (
              <label className="block mb-3"><span className="text-sm text-[#5B6675] mb-1.5 block">Onderwerp</span>
                <input className="fe-input" value={t.subject || ""} onChange={(e) => set(t.id, "subject", e.target.value)} /></label>
            )}
            <label className="block"><span className="text-sm text-[#5B6675] mb-1.5 block">Bericht</span>
              <textarea className="fe-input resize-y leading-relaxed" rows={7} value={t.body} onChange={(e) => set(t.id, "body", e.target.value)} /></label>
            <div className="flex justify-end gap-2 mt-3">
              <button data-testid={`del-template-${t.id}`} onClick={() => remove(t.id)} className="p-2 rounded-lg hover:bg-[#F3DEDD] text-[#B0413E]"><Trash2 className="h-4 w-4" /></button>
              <button data-testid={`save-template-${t.id}`} onClick={() => save(t)} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#001C51] text-white text-sm font-medium hover:bg-[#0A337A]"><Save className="h-4 w-4" /> Opslaan</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const PERM_TABS = { scripts: ["scripts", "objections"], services_kb: ["services", "kb"] };

export default function Beheer() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const allowed = (user?.permissions || []).flatMap((p) => PERM_TABS[p] || []);
  const show = (id) => isAdmin || allowed.includes(id);
  const [tab, setTab] = useState(isAdmin ? "scripts" : allowed[0] || "scripts");
  const [rinkel, setRinkel] = useState(null);
  useEffect(() => { if (isAdmin) getRinkelStatus().then(setRinkel).catch(() => setRinkel({ configured: false })); }, [isAdmin]);

  const reset = async () => {
    if (!window.confirm("Alle scripts, antwoorden én berichten terugzetten naar de standaardteksten? Je eigen wijzigingen gaan verloren.")) return;
    await resetContent();
    toast.success("Teruggezet naar standaard");
    window.location.reload();
  };

  return (
    <div className="px-6 lg:px-12 py-10 max-w-4xl mx-auto fe-fade-up">
      <header className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-[#16202B]">Beheer teksten</h1>
          <p className="text-[#5B6675] mt-2">Pas je belscripts en antwoorden zelf aan. Wijzigingen zijn direct actief.</p>
          {isAdmin && <button data-testid="rinkel-badge-btn" onClick={() => setTab("rinkel")} className="mt-3"><RinkelBadge status={rinkel} /></button>}
        </div>
        {isAdmin && <button data-testid="reset-content" onClick={reset} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#E2E7EC] text-[#5B6675] font-medium hover:bg-[#EDF1F5]">
          <RotateCcw className="h-4 w-4" /> Standaard herstellen
        </button>}
      </header>

      <div className="inline-flex flex-wrap rounded-xl border border-[#E2E7EC] bg-white p-1 mb-8">
        {show("scripts") && <button data-testid="tab-scripts" onClick={() => setTab("scripts")} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === "scripts" ? "bg-[#001C51] text-white" : "text-[#5B6675] hover:bg-[#EDF1F5]"}`}><FileText className="h-4 w-4" /> Belscripts</button>}
        {show("objections") && <button data-testid="tab-objections" onClick={() => setTab("objections")} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === "objections" ? "bg-[#001C51] text-white" : "text-[#5B6675] hover:bg-[#EDF1F5]"}`}><MessageSquareQuote className="h-4 w-4" /> Antwoorden</button>}
        {show("templates") && <button data-testid="tab-templates" onClick={() => setTab("templates")} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === "templates" ? "bg-[#001C51] text-white" : "text-[#5B6675] hover:bg-[#EDF1F5]"}`}><Send className="h-4 w-4" /> Berichten</button>}
        {show("team") && <button data-testid="tab-team" onClick={() => setTab("team")} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === "team" ? "bg-[#001C51] text-white" : "text-[#5B6675] hover:bg-[#EDF1F5]"}`}><Users2 className="h-4 w-4" /> Teams & gebruikers</button>}
        {show("pipeline") && <button data-testid="tab-pipeline" onClick={() => setTab("pipeline")} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === "pipeline" ? "bg-[#001C51] text-white" : "text-[#5B6675] hover:bg-[#EDF1F5]"}`}><SlidersHorizontal className="h-4 w-4" /> Pipeline</button>}
        {show("branding") && <button data-testid="tab-branding" onClick={() => setTab("branding")} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === "branding" ? "bg-[#001C51] text-white" : "text-[#5B6675] hover:bg-[#EDF1F5]"}`}><ImageIcon className="h-4 w-4" /> Branding</button>}
        {show("services") && <button data-testid="tab-services" onClick={() => setTab("services")} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === "services" ? "bg-[#001C51] text-white" : "text-[#5B6675] hover:bg-[#EDF1F5]"}`}><Package className="h-4 w-4" /> Diensten</button>}
        {show("kb") && <button data-testid="tab-kb" onClick={() => setTab("kb")} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === "kb" ? "bg-[#001C51] text-white" : "text-[#5B6675] hover:bg-[#EDF1F5]"}`}><BookOpen className="h-4 w-4" /> Kennisbank</button>}
        {show("rinkel") && <button data-testid="tab-rinkel" onClick={() => setTab("rinkel")} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === "rinkel" ? "bg-[#001C51] text-white" : "text-[#5B6675] hover:bg-[#EDF1F5]"}`}><PhoneCall className="h-4 w-4" /> Telefonie</button>}
        {show("import") && <button data-testid="tab-import" onClick={() => setTab("import")} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === "import" ? "bg-[#001C51] text-white" : "text-[#5B6675] hover:bg-[#EDF1F5]"}`}><Upload className="h-4 w-4" /> Leadimport</button>}
      </div>

      {tab === "scripts" && <ScriptEditor />}
      {tab === "objections" && <ObjectionEditor />}
      {tab === "templates" && <TemplateEditor />}
      {tab === "team" && <TeamUserEditor />}
      {tab === "pipeline" && <PipelineConfigEditor />}
      {tab === "branding" && <BrandingEditor />}
      {tab === "services" && <ServiceEditor />}
      {tab === "kb" && <KbEditor />}
      {tab === "rinkel" && <RinkelPanel />}
      {tab === "import" && <LeadImportPanel />}
    </div>
  );
}
